import { heardConfig } from './apps/heard.config'
import { lamourConfig } from './apps/lamour.config'
import { bibleConfig } from './apps/bible.config'
import { readingPlusConfig } from './apps/reading-plus.config'
import { seenConfig } from './apps/seen.config'
import { mxBooksConfig } from './apps/mx-books.config'
import { motorsportConfig } from './apps/motorsport.config'
import type { AppConfig } from '@/types/config'

export * from './sizes'

const configs: Record<string, AppConfig> = {
  heard: heardConfig,
  lamour: lamourConfig,
  bible: bibleConfig,
  'reading-plus': readingPlusConfig,
  seen: seenConfig,
  'mx-books': mxBooksConfig,
  motorsport: motorsportConfig
}

export const getConfig = (): AppConfig => {
  const appId = import.meta.env.VITE_APP || 'heard'
  const baseConfig = configs[appId] || configs.heard
  // Override epubReader flag from environment only if explicitly set
  const envEpubReader = import.meta.env.VITE_EPUB_READER
  const epubReaderFlag = envEpubReader ? String(envEpubReader).toLowerCase() === 'true' : baseConfig.features.epubReader
  // Override video flag from environment only if explicitly set
  const envVideo = import.meta.env.VITE_VIDEO
  const videoFlag = envVideo ? String(envVideo).toLowerCase() === 'true' : baseConfig.features.video
  const envPdfReader = import.meta.env.VITE_PDF_READER
  const pdfReaderFlag = envPdfReader
    ? String(envPdfReader).toLowerCase() === 'true'
    : Boolean(baseConfig.features.pdfReader)
  return {
    ...baseConfig,
    features: {
      ...baseConfig.features,
      epubReader: epubReaderFlag,
      video: videoFlag,
      pdfReader: pdfReaderFlag
    }
  }
}

/** Video-first apps (e.g. Seen): no EPUB/PDF reader, bookmarks are hidden. */
export function isVideoOnlyApp(config: AppConfig = getConfig()): boolean {
  return Boolean(config.features.video && !config.features.epubReader && !config.features.pdfReader)
}

export type CoverAspectRatio = 'square' | 'portrait' | 'landscape'

// Resolve the aspect ratio used for content covers/posters.
// EPUB reader apps always use portrait book covers; otherwise honour the
// configured `coverAspectRatio`, defaulting to square.
export const getCoverAspectRatio = (): CoverAspectRatio => {
  const config = getConfig()
  if (config.features.epubReader) return 'portrait'
  return config.features.coverAspectRatio ?? 'square'
}

// Helper functions to get specific config sections
export const getThemeConfig = () => getConfig().theme
export const getLayoutConfig = () => getConfig().layout
export const getFeaturesConfig = () => getConfig().features
export const getApiConfig = () => getConfig().api 